import type { ReactNode } from "react";
import { Children } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { STAGGER_CHILD, STAGGER_PARENT } from "./motion";

interface ZoneSectionProps {
  name: string;
  /** Optional summary chips (SensorBadge / StatusPill) shown next to the name. */
  summary?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Zone header plus a responsive, stagger-animated tile grid.
 * Children rendered from arrays must carry keys. The stagger entrance runs
 * once on mount by design; device updates do not replay it.
 */
export function ZoneSection({ name, summary, children, className }: ZoneSectionProps) {
  return (
    <section data-slot="zone-section" className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{name}</h2>
        {summary && <div className="flex items-center gap-1.5">{summary}</div>}
      </div>
      <motion.div
        variants={STAGGER_PARENT}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4"
      >
        {Children.map(children, (child) => (
          <motion.div variants={STAGGER_CHILD}>{child}</motion.div>
        ))}
      </motion.div>
    </section>
  );
}
